import React, { useEffect, useMemo, useState } from 'react';
import { MapContainer, Marker, TileLayer, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Leaflet's default marker points at image files that a bundler rewrites, which is
// the classic "marker is invisible" bug. A div icon has no assets at all.
const PIN = L.divIcon({
  className: '',
  html:
    '<div style="width:22px;height:22px;border-radius:50% 50% 50% 0;background:#dc2626;' +
    'border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.4);transform:rotate(-45deg)"></div>',
  iconSize: [22, 22],
  iconAnchor: [11, 22],
});

// Roughly the middle of India — where a new pin starts when a ground has no
// location yet, so the admin is at least on the right continent.
const FALLBACK: [number, number] = [22.9734, 78.6569];
const FALLBACK_ZOOM = 4;
const PLACED_ZOOM = 16;

interface Props {
  lat: number | null;
  lng: number | null;
  onChange: (lat: number, lng: number) => void;
}

/** Click anywhere to move the pin. */
const ClickToPlace: React.FC<{ onChange: (lat: number, lng: number) => void }> = ({ onChange }) => {
  useMapEvents({ click: (e) => onChange(e.latlng.lat, e.latlng.lng) });
  return null;
};

/** Recentre when the coordinates change from outside (search, typed input). */
const Recentre: React.FC<{ lat: number | null; lng: number | null }> = ({ lat, lng }) => {
  const map = useMap();
  useEffect(() => {
    if (lat != null && lng != null) map.setView([lat, lng], Math.max(map.getZoom(), PLACED_ZOOM));
  }, [lat, lng, map]);
  return null;
};

const MapPicker: React.FC<Props> = ({ lat, lng, onChange }) => {
  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState<Array<{ label: string; lat: number; lon: number }>>([]);
  const [error, setError] = useState<string | null>(null);

  const centre = useMemo<[number, number]>(
    () => (lat != null && lng != null ? [lat, lng] : FALLBACK),
    [lat, lng],
  );

  // OpenStreetMap's geocoder: free, no key, but rate-limited — so it runs on
  // submit, never per keystroke. The pin stays the source of truth; this is only
  // a way to fly to roughly the right place.
  const search = async (e: React.FormEvent) => {
    e.preventDefault();
    const q = query.trim();
    if (!q) return;
    setSearching(true);
    setError(null);
    setResults([]);
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&limit=5&q=${encodeURIComponent(q)}`,
        { headers: { Accept: 'application/json' } },
      );
      if (!res.ok) throw new Error(String(res.status));
      const json = await res.json();
      if (!Array.isArray(json) || json.length === 0) {
        setError('No place matched that. Try a nearby landmark, or just drop the pin.');
        return;
      }
      setResults(json.map((r: any) => ({ label: r.display_name, lat: Number(r.lat), lon: Number(r.lon) })));
    } catch {
      setError('Address lookup is unavailable right now — drop the pin manually.');
    } finally {
      setSearching(false);
    }
  };

  return (
    <div>
      <form onSubmit={search} className="flex gap-2 mb-2">
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search an address or landmark…"
          className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm"
        />
        <button type="submit" disabled={searching || !query.trim()}
          className="text-sm font-medium px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-60">
          {searching ? 'Searching…' : 'Search'}
        </button>
      </form>

      {results.length > 0 && (
        <ul className="mb-2 max-h-32 overflow-y-auto rounded-lg border border-gray-200 divide-y divide-gray-100">
          {results.map((r, i) => (
            <li key={i}>
              <button type="button"
                onClick={() => { onChange(r.lat, r.lon); setResults([]); }}
                className="w-full text-left px-3 py-2 text-xs text-gray-700 hover:bg-gray-50">
                {r.label}
              </button>
            </li>
          ))}
        </ul>
      )}
      {error && <p className="text-xs text-amber-700 mb-2">{error}</p>}

      <div className="h-64 rounded-lg overflow-hidden border border-gray-200">
        <MapContainer center={centre} zoom={lat != null ? PLACED_ZOOM : FALLBACK_ZOOM} style={{ height: '100%', width: '100%' }}>
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <ClickToPlace onChange={onChange} />
          <Recentre lat={lat} lng={lng} />
          {lat != null && lng != null && (
            <Marker
              position={[lat, lng]}
              icon={PIN}
              draggable
              eventHandlers={{
                dragend: (e) => {
                  const p = (e.target as L.Marker).getLatLng();
                  onChange(p.lat, p.lng);
                },
              }}
            />
          )}
        </MapContainer>
      </div>
      <p className="text-xs text-gray-400 mt-1">
        {lat != null ? 'Drag the pin or click the map to move it.' : 'Click the map to place the ground.'}
      </p>
    </div>
  );
};

export default MapPicker;
